<script>
import {Carousel, Slide} from "vue-carousel";
import axios from "axios";
import { getDefaultThumbnail, proofFilePreviewPending } from "../../utils/helpers";

export default {
  name: "thumbnails",
  props: {
    proof: {
      required: true,
      type: Object,
    },
  },
  components: {
    Carousel,
    Slide,
  },
  beforeCreate() {
    // Keep requests/timers out of Vue's observed state.
    this.thumbnailPolls = new Map();
  },
  watch: {
    versionFiles: {
      immediate: true,
      deep: true,
      handler() {
        this.syncThumbnailPolls();
      },
    },
  },
  beforeDestroy() {
    this.thumbnailPolls.forEach((poll, id) => this.stopThumbnailPoll(id));
  },
  methods: {
    getThumbnailUrl: function(thumbnail) {
      if (thumbnail.thumbnailFileKey !== '' &&
          thumbnail.thumbnailFileKey.indexOf('static') === -1 &&
          thumbnail.thumbnailFileKey.indexOf('public') === -1) {
        return 'url(https://cdn.ashoreapp.com/' + thumbnail.thumbnailFileKey + ')';
      } else if (thumbnail.thumbnailFileKey !== '') {
        if (thumbnail.thumbnailFileKey.startsWith('public/icon/'))
          return 'url(' + thumbnail.thumbnailFileKey.replace('public/icon/', '/img/') + ')';
        return 'url(' + thumbnail.thumbnailFileKey + ')';
      }
      return 'url(/img/thumbnail-generating.svg)';
    },
    syncThumbnailPolls() {
      const pending = this.versionFiles.filter((file) => file.id > 0 && proofFilePreviewPending(file));
      this.thumbnailPolls.forEach((poll, id) => {
        if (!pending.some((file) => file.id === id)) this.stopThumbnailPoll(id);
      });
      pending.forEach((file) => {
        if (this.thumbnailPolls.has(file.id)) return;
        const poll = { cancel: axios.CancelToken.source(), timer: null };
        this.thumbnailPolls.set(file.id, poll);
        this.longPollForUpdate(file.id, poll);
      });
    },
    stopThumbnailPoll(id) {
      const poll = this.thumbnailPolls.get(id);
      if (!poll) return;
      clearTimeout(poll.timer);
      poll.cancel.cancel();
      this.thumbnailPolls.delete(id);
    },
    async longPollForUpdate(id, poll) {
      const current = this.versionFiles.find((file) => file.id === id);
      if (!current || !proofFilePreviewPending(current)) {
        this.stopThumbnailPoll(id);
        return;
      }
      const file = await this.$A.ProofFileService.WaitForThumbnailToLoad(id, poll.cancel.token);
      // A response for a removed file/version or a destroyed component is stale.
      if (this.thumbnailPolls.get(id) !== poll) return;
      const index = this.versionFiles.findIndex((item) => item.id === id);
      if (index === -1) {
        this.stopThumbnailPoll(id);
        return;
      }
      if (file && file.id === id) {
        // Replace in the actual observed array; filter()[0] only writes to a copy.
        this.versionFiles.splice(index, 1, file);
        if (!proofFilePreviewPending(file)) {
          this.stopThumbnailPoll(id);
          if (!this.versionFiles.some(proofFilePreviewPending)) {
            this.$root.$emit("proof::refresh", {});
          }
          return;
        }
      }
      // Long polls can expire or fail before conversion finishes. Retry with a
      // delay, bounded by the same processing window used by the preview UI.
      poll.timer = setTimeout(() => this.longPollForUpdate(id, poll), 5000);
    },
    getDefaultThumbnail,
    isPending: proofFilePreviewPending,
    noPreview(file) {
      const hasRealThumb = !!file.thumbnailFileKey && file.thumbnailFileKey.indexOf("static") === -1;
      return !hasRealThumb && !proofFilePreviewPending(file);
    },
    nullThumbSub(file) {
      return file.docTypeGroup === "audio" ? "Audio files don't have a visual preview." : "";
    },
  },
  computed: {
    versionFiles: function() {
      return _.get(
          this.proof,
          'currentVersion.versionFiles',
          []
      ) || [];
    }
  }
};
</script>

<template>
  <!-- Thumbnails -->
  <div class="col-md-12 px-0" >
    <b-carousel ref="carousel" id="carousel-fade" slide indicators>
      <b-carousel-slide v-for="thumbnail in versionFiles" :key="thumbnail.id" :interval="4000">
        <template v-slot:img>
          <div v-if="noPreview(thumbnail)" class="carousel-null-thumb">
            <div class="null-thumb-icon">
              <svg v-if="thumbnail.docTypeGroup === 'audio'" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                <path d="M9 18V5l12-2v13"/>
                <circle cx="6" cy="18" r="3"/>
                <circle cx="18" cy="16" r="3"/>
              </svg>
              <svg v-else-if="thumbnail.docTypeGroup === 'video'" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="3"/>
                <path d="M10 9.5l5 2.5-5 2.5z"/>
              </svg>
              <svg v-else width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z"/>
                <path d="M14 2v5h5"/>
                <path d="M9 13h6M9 17h4"/>
              </svg>
            </div>
            <div class="null-thumb-title">No preview available</div>
            <div v-if="nullThumbSub(thumbnail)" class="null-thumb-sub">{{ nullThumbSub(thumbnail) }}</div>
          </div>
          <template v-else>
            <div class="carousel-ashore-image" :style="{backgroundImage: thumbnail.thumbnailFileKey ? getThumbnailUrl(thumbnail) : `url(${getDefaultThumbnail(thumbnail.docTypeGroup)})`}"></div>
            <p class="loading-text" v-if="isPending(thumbnail)">
              File Processing
            </p>
          </template>
        </template>
      </b-carousel-slide>
    </b-carousel>
  </div>
</template>
<style scoped>

.carousel-ashore-image {
  width: 100%;
  height: 280px;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
}

.carousel-null-thumb {
  width: 100%;
  height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 16px;
}
.null-thumb-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  margin-bottom: 10px;
}
.null-thumb-title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
  line-height: 1.2;
}
.null-thumb-sub {
  font-size: 11px;
  color: #6b7280;
  margin-top: 3px;
}

::v-deep .carousel {
  border-radius: 8px;
  height: 300px;
  width: 100%;
  padding: 10px;
  background: #EEF2F5;
}

.loading-text {
  position: absolute;
  top: 75%;
  left: 5%;
  right: 5%;
  font-weight: bold;
  text-align: center;
  background: #ffffff;
  padding: 10px;
  width: 90%;
  border-radius: 4px;
  box-shadow: 0 -3px 10px 0 rgba(106, 139, 148, 0.2);
}


</style>
